var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Utils, ExceptionService } from '@busuu/legacy-core';
import GTM from 'tracking/tracking-gtm.js';
import Snowplow from 'tracking/tracking-snowplow.js';
import Floodlight from 'tracking/tracking-floodlight';
import TrackingEventsConstants from 'tracking/tracking-constants';
import CurrentUser from 'user/current-user-service.js';
import QueryParameters from 'helpers/query-parameters.js';
import MobileService from 'mobile/mobile-service';
import { getWebAttribution, getWebPrevAttribution, storeWebAttribution } from '@busuu/marketing';
import { getSnowplowDuid } from '@busuu/tracking';
import { getParameter } from '@busuu/query-params';
import { OneTrustCookieConsentService } from '@busuu/cookie-consent';
import ConfigService from 'config/config-service';
import { initAmplitudeSDKsWithUser } from '@busuu/experiments';
import { ERROR_NETWORK_ISSUE } from 'endpoint/api-requests-service.constants.js';
// Vars
let currentUserID = null;
let isAnonymousAmplitudeInitialized = false;
let anonymousAmplitudeInitPromise = null;
const trackingProviders = [
    { name: 'GTM', provider: GTM, logs: false },
    { name: 'Snowplow', provider: Snowplow, logs: false },
    { name: 'Floodlight', provider: Floodlight, logs: false },
];
const filename = 'tracking-controller.ts';
/**
 * Logs an error and optionally to Sentry with type: 'error'
 * @param {string} message
 * @param {object} error
 * @param {string} type
 */
const logError = (message, error, type = 'warning') => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    ExceptionService.handle(type, {
        filename: filename,
        message,
        data: error,
        sendToSentry: !!(type === 'error'),
    });
};
/**
 * Determines the appropriate log type based on error type
 * @param {unknown} error - The error object
 * @returns {string} - The log type ('error' or 'warning')
 */
const getLogType = (error) => {
    const errorHasType = typeof error === 'object' && error !== null && 'type' in error;
    if (errorHasType) {
        const errorWithType = error;
        return errorWithType.type === ERROR_NETWORK_ISSUE.applicationCode ? 'warning' : 'error';
    }
    return 'error';
};
/**
 * Gets Amplitude API keys from environment variables
 * @returns {object} - Object containing analyticsKey and experimentsDeploymentKey
 */
const getAmplitudeKeys = () => {
    return {
        analyticsKey: process.env.AMPLITUDE_ANALYTICS_KEY,
        experimentsDeploymentKey: process.env.AMPLITUDE_EXPERIMENTS_DEPLOYMENT_KEY,
    };
};
/**
 * We usually always want to store the attribution cookie that stores
 * our UTMs and GCLIDs but there's some special cases where we don't:
 *
 * - When a user tries to access a logged-in page and gets redirected to `/logout` with a `redirect` param (because the UTMs are URL encoded in the redirect value)
 *
 * @returns {boolean}
 */
const shouldSkipStoringAttribution = () => {
    return !!getParameter('redirect');
};
/**
 * Returns an event template from an event name
 * @private
 */
const getEventTemplate = (eventName) => {
    const eventTemplate = TrackingEventsConstants[eventName];
    return eventTemplate;
};
/**
 * Extends the event parameters with common params.
 * @private
 */
const extendWithCommonData = (params) => {
    /** Session/Vistor ID are currently pulled from Snowplow. */
    const { domain_sessionid: sessionId, domain_userid: visitorId } = getSnowplowDuid() || {
        domain_sessionid: '',
        domain_userid: '',
    };
    const attribution = getWebAttribution() || {};
    const prevAttribution = getWebPrevAttribution() || {}; // This is an attribution for debugging https://busuucom.atlassian.net/browse/GA-2313
    if (attribution.error)
        logError('extendWithCommonData() - getWebAttribution() resulted in error.', attribution.error, 'error');
    if (prevAttribution.error)
        logError('extendWithCommonData() - getWebPrevAttribution() resulted in error.', prevAttribution.error, 'error');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, Utils.deepClone(params)), (MobileService.isAuthenticationOrRegistrationMobileFlow() && {
        auth_type: QueryParameters.getMobileAuthParameter(),
    })), Utils.deepClone(QueryParameters.getInstitutionParameters())), { 
        /** Attaches `session_id` */
        session_id: sessionId || '', 
        /** Attaches `visitor_id` */
        visitor_id: visitorId || '', attribution: Object.assign(Object.assign({}, ((window === null || window === void 0 ? void 0 : window.BUSUU_PAGE_NAMING) || {})), (!attribution.error ? attribution : {})) }), (!prevAttribution.error && { prev_attribution: prevAttribution }));
};
/**
 * Checks if we can send the event to the tracking service
 */
const canSendEvent = (eventTemplate, trackingServiceName) => {
    const isExcluded = eventTemplate.exclusions.includes(trackingServiceName);
    return !isExcluded;
};
/**
 * Send an event to all tracking provider
 * @private
 */
const sendEvent = (eventName, data) => {
    /** Always store web cookie (if needed) before sending an event */
    const result = storeWebAttribution({ debug: false, skipStoringCookie: shouldSkipStoringAttribution() });
    if (result && result.error)
        logError('sendEvent() - storeWebAttribution() resulted in error.', result.error, 'error');
    const eventTemplate = getEventTemplate(eventName);
    const params = extendWithCommonData(data);
    trackingProviders.forEach(({ name, provider }) => {
        if (canSendEvent(eventTemplate, name)) {
            provider.sendEvent(eventTemplate, params);
        }
    });
};
const buildUserParams = (user) => {
    var _a, _b, _c;
    const institution = user.institution;
    const languages = user.languages;
    return {
        uid: user.uid,
        role: (_b = (_a = user.access) === null || _a === void 0 ? void 0 : _a.tier) !== null && _b !== void 0 ? _b : 'free',
        country: user.country,
        countryCode: user.country_code || '',
        courseLang: (languages === null || languages === void 0 ? void 0 : languages.learning_default) || 'en',
        intLang: (languages === null || languages === void 0 ? void 0 : languages.interface) || 'en',
        created: (_c = user.created) !== null && _c !== void 0 ? _c : undefined,
        institutionRole: institution === null || institution === void 0 ? void 0 : institution.institution_role,
        institutionId: (institution === null || institution === void 0 ? void 0 : institution.institution_id) != null ? Number(institution.institution_id) : undefined,
    };
};
/**
 * When user is recognised, we add extra data to trackers.
 * We add a logic to avoid multiple authentications
 * based on the current user id. In some cases this ID can change
 * and a new user needs to be identified. (e.g.: registration)
 * @private
 */
const identifyUser = (user) => {
    if (currentUserID === user.uid) {
        return false;
    }
    // Add Sentry user context
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    ExceptionService.identifyUser(user);
    trackingProviders.forEach(({ provider }) => {
        provider.identifyUser(user);
    });
    const { analyticsKey, experimentsDeploymentKey } = getAmplitudeKeys();
    const userParams = buildUserParams(user);
    if (analyticsKey && experimentsDeploymentKey) {
        void initAmplitudeSDKsWithUser(analyticsKey, experimentsDeploymentKey, userParams, false);
    }
    // Store userID to avoid multiple authentications
    currentUserID = user.uid;
};
// Initialize Amplitude for anonymous users with country context (when CurrentUser.get() fails)
const initializeAmplitudeForAnonymousUser = (countryCode) => __awaiter(void 0, void 0, void 0, function* () {
    const { analyticsKey, experimentsDeploymentKey } = getAmplitudeKeys();
    if (analyticsKey && experimentsDeploymentKey) {
        try {
            // Create minimal user props for anonymous user with country context
            const anonymousUserProps = {
                uid: null,
                country: null,
                countryCode,
                role: 'free',
                courseLang: null,
                intLang: null,
                created: 0,
                institutionRole: null,
                institutionId: null,
            };
            yield initAmplitudeSDKsWithUser(analyticsKey, experimentsDeploymentKey, anonymousUserProps, false);
        }
        catch (e) {
            const logType = getLogType(e);
            logError('initializeAmplitudeForAnonymousUser() - Failed to initialize Amplitude for anonymous user.', e, logType);
        }
    }
});
// Ensure Amplitude is initialized once for anonymous users
const ensureAnonymousAmplitudeInitialized = () => __awaiter(void 0, void 0, void 0, function* () {
    if (isAnonymousAmplitudeInitialized)
        return;
    if (anonymousAmplitudeInitPromise !== null)
        return anonymousAmplitudeInitPromise;
    anonymousAmplitudeInitPromise = (() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const countryCode = yield ConfigService.getCountryCode();
            yield initializeAmplitudeForAnonymousUser(countryCode);
        }
        catch (e) {
            const logType = getLogType(e);
            logError('initializeTrackingForAnonymousUser() - Failed to get country code.', e, logType);
            yield initializeAmplitudeForAnonymousUser('').catch(() => {
                // Ignore Amplitude initialization errors in fallback
            });
        }
        finally {
            isAnonymousAmplitudeInitialized = true;
            anonymousAmplitudeInitPromise = null;
        }
    }))();
    return anonymousAmplitudeInitPromise;
});
// Initialize Amplitude for anonymous users
const initializeTrackingForAnonymousUser = () => __awaiter(void 0, void 0, void 0, function* () {
    return ensureAnonymousAmplitudeInitialized();
});
/**
 * Middleware wrapper before sending events.
 * This function tries to get some information
 * about the user first and then send the event,
 * so we can ensure that we have user information
 * along with the event when it's available.
 * @public
 */
const send = (eventName, data = {}) => {
    const onComplete = () => {
        sendEvent(eventName, data);
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    CurrentUser.get()
        .then(identifyUser)
        .catch(
    // Fall back to initialize Amplitude for anonymous users if current user service call fails.
    // Not strictly necessary since anonymous initialization is triggered during bootstrap,
    // but kept as a safety net in case bootstrap fails or there is a race condition.
    initializeTrackingForAnonymousUser)
        .then(onComplete);
};
/**
 * Shortcut handler for send() that wraps a promise
 * that always resolves. It ensures the tracking has been
 * sent using a delay timeout to let enough time
 * for the tracking request to process.
 * @public
 */
const sendAndWait = (eventName, data = {}) => {
    send(eventName, data);
    return new Promise((resolve) => {
        window.setTimeout(resolve, 750);
    });
};
const sendCookieTracking = (trackingEvent, params) => {
    const eventParams = {};
    for (const key in params) {
        if (key === 'platform' || key === 'source_page' || key === 'cookies') {
            eventParams[key] = params[key];
        }
    }
    return send(trackingEvent, eventParams);
};
// Timeout workaround to ensure window.OneTrust is loaded
const initCookieTracking = () => {
    setTimeout(() => {
        OneTrustCookieConsentService.init(sendCookieTracking);
    }, 2000);
};
const init = () => {
    /**
     * When initialising tracking, ensure that the web attribution cookie is stored
     */
    const result = storeWebAttribution({ debug: false, skipStoringCookie: shouldSkipStoringAttribution() });
    if (result && result.error)
        logError('init() - storeWebAttribution() resulted in error.', result.error, 'error');
    trackingProviders.forEach(({ provider: { load }, logs }) => {
        if (load) {
            load({ logs });
        }
    });
    // Init Amplitude as anonymous first; Overwrite if user is logged in.
    void ensureAnonymousAmplitudeInitialized();
};
init();
const TrackingController = {
    send,
    sendAndWait,
    initCookieTracking,
};
export default TrackingController;
